import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Modal } from "./Modal";
import { Alert, Button, Field, Input, Select } from "./ui";
import { ApiError } from "../lib/api";
import { engineSpec } from "../lib/engines";
import { useApps, useAttachDatabase } from "../lib/queries";
import { regionLabel } from "../lib/regions";
import type { Application, Database } from "../lib/types";

/**
 * Stays a dialog rather than becoming a tab: attaching is a short decision
 * taken and finished, not a screen anyone sits with.
 */
export function AttachDatabaseModal({
  orgId,
  database,
  onClose,
  onDone,
}: {
  orgId: string | undefined;
  database: Database | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation("database");
  /*
   * The maximum one page allows. A select is the wrong control past that many
   * anyway, and the note below says so rather than letting the list quietly
   * stop at a hundred.
   */
  const apps = useApps(orgId, 100);
  const options = apps.data?.data ?? [];
  const hidden = (apps.data?.total ?? 0) - options.length;
  const attach = useAttachDatabase(orgId ?? "");

  /*
   * An app reaches this database by its internal hostname only from the
   * same region — the name is a container on one server's network. Apps
   * elsewhere stay in the list, named with their region and not choosable,
   * so the customer sees why rather than getting the API's refusal after
   * picking one. Rows without a region predate placements and were placed
   * beside each other.
   */
  const reachable = (app: Application) =>
    !database?.region || !app.region || app.region === database.region;
  const elsewhere = options.some((app) => !reachable(app));

  const [appId, setAppId] = useState("");
  const [key, setKey] = useState("DATABASE_URL");
  const [redeploy, setRedeploy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redis is a cache, not a database URL; defaulting to DATABASE_URL for it
  // would be actively misleading. Reset on open rather than during render,
  // which would fight anything the customer typed.
  useEffect(() => {
    if (database) setKey(engineSpec(database.engine).envKey);
  }, [database]);

  async function submit() {
    if (!database || !appId) {
      setError(t("attach.pickApp"));
      return;
    }
    setError(null);
    try {
      const result = await attach.mutateAsync({ databaseId: database.id, appId, key, redeploy });
      onDone(
        result.redeployed
          ? t("attach.addedRedeploying", { key })
          : t("attach.addedRedeployNeeded", { key }),
      );
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("attach.failed"));
    }
  }

  return (
    <Modal
      open={database !== null}
      onClose={onClose}
      title={t("attach.title", { name: database?.name ?? "" })}
      description={t("attach.writes")}
    >
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Field
          label={t("attach.app")}
          htmlFor="attach-app"
          hint={
            elsewhere
              ? t("attach.otherRegion")
              : undefined
          }
        >
          <Select id="attach-app" value={appId} onChange={(e) => setAppId(e.target.value)}>
            <option value="">{t("attach.choose")}</option>
            {options.map((app) => (
              <option key={app.id} value={app.id} disabled={!reachable(app)}>
                {reachable(app) ? app.name : `${app.name} · ${regionLabel(app.region)}`}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t("attach.variable")} htmlFor="attach-key" hint={t("attach.overwrites")}>
          <Input id="attach-key" value={key} onChange={(e) => setKey(e.target.value)} />
        </Field>

        <label className="flex items-center gap-2 text-sm text-fg-muted">
          <input
            type="checkbox"
            checked={redeploy}
            onChange={(e) => setRedeploy(e.target.checked)}
            className="size-4 rounded border-line text-accent"
          />
          {t("attach.redeploy")}
        </label>

        {hidden > 0 && (
          <p className="text-xs text-fg-subtle">
            Showing the {options.length} most recent apps. {hidden} older not listed.
          </p>
        )}

        {options.length === 0 && (
          <Alert tone="info">
            You have no apps yet.{" "}
            <Link to="/apps/new" className="underline">
              {t("attach.createApp")}
            </Link>{" "}
            and then attach this database to it.
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("attach.cancel")}
          </Button>
          <Button loading={attach.isPending} disabled={!appId} onClick={() => void submit()}>
            {t("attach.attach")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
